// The entrance's one picture (app/art/welcome/PLAN.md section 6): the home
// page's vault chip (src/site/hero/vaultChip.js, unchanged) drawn once as a
// still at the power-up frame p 2.69, when the bolts are home and every
// one-time key on the card shows available, none spent. Nothing moves, so
// there is no Pause and nothing for reduced motion to stop.
// - one draw, no layer store: the canvas's CSS box sets its width and height
//   attributes at a pixel ratio of at most 2, then drawV9 paints p 2.69;
// - it redraws only when the box changes size (a ResizeObserver, coalesced
//   to one animation frame) or the screen's pixel ratio changes;
// - the stage is aria-hidden and holds nothing focusable; its edges are
//   masks in entrance.css (CSP-safe), and it is never wired to session or
//   chain state, so it asserts nothing about this vault.

import { useEffect, useRef } from 'react';
import { drawV9, kit, type Fit } from '../site/hero/vaultChipZq';

/** The power-up moment the still shows: after the bolts, before the loop's first spend at 2.7. */
export const ENTRANCE_P = 2.69;
const MAX_DPR = 2;
/** Below this canvas width the scene draws its phone variant (as the home hero). */
const PHONE_BELOW = 520;

/** World windows from the Continuity mock (PLAN.md section 6, "Fits"). */
export const ENTRANCE_FITS: Record<'stage' | 'stagePhone' | 'small' | 'smallPhone', Fit> = {
  stage: [-9.6, 8.8, -1.1, 5.3],
  stagePhone: [-6.4, 5.6, -1.6, 5.3],
  small: [-5.9, 5.1, -1.1, 5.1],
  smallPhone: [-5.6, 5.0, -1.3, 5.1],
};

export function entranceFit(size: 'stage' | 'small', phone: boolean): Fit {
  if (size === 'small') return phone ? ENTRANCE_FITS.smallPhone : ENTRANCE_FITS.small;
  return phone ? ENTRANCE_FITS.stagePhone : ENTRANCE_FITS.stage;
}

export function EntranceArt({ size = 'stage' }: { size?: 'stage' | 'small' }) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    let frame = 0;
    let ratio: MediaQueryList | null = null;

    const draw = () => {
      frame = 0;
      const r = canvas.getBoundingClientRect();
      if (!r.width || !r.height) return;
      const dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
      canvas.width = Math.round(r.width * dpr);
      canvas.height = Math.round(r.height * dpr);
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const phone = r.width < PHONE_BELOW;
      drawV9(kit({ width: r.width, height: r.height, getContext: () => ctx }, entranceFit(size, phone)), 0, { p: ENTRANCE_P, phone, flatten: false });
      canvas.dataset.ready = 'true';
      watchRatio();
    };
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(draw);
    };
    // a pixel-ratio change (a zoom, or the window moved to another screen) matches a new resolution query
    const watchRatio = () => {
      ratio?.removeEventListener('change', schedule);
      ratio = window.matchMedia(`(resolution: ${window.devicePixelRatio || 1}dppx)`);
      ratio.addEventListener('change', schedule);
    };

    const ro = new ResizeObserver(schedule);
    ro.observe(canvas);
    return () => {
      ro.disconnect();
      cancelAnimationFrame(frame);
      ratio?.removeEventListener('change', schedule);
    };
  }, [size]);

  return (
    <div className={`entry-stage${size === 'small' ? ' small' : ''}`} aria-hidden="true">
      <canvas ref={ref} />
    </div>
  );
}
